import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { BRAND } from "@/content";
import { useLang } from "@/LangContext";
import { scrollToId } from "@/lib/royale";
import LotusMark from "./LotusMark";

function LangToggle({ className = "" }) {
  const { lang, setLang } = useLang();
  const btn = (code, label) => (
    <button
      key={code}
      data-testid={`language-toggle-${code}`}
      onClick={() => setLang(code)}
      className={`font-mono text-xs tracking-[0.18em] uppercase px-2 py-1 transition-colors duration-300 ${
        lang === code ? "text-goldbright" : "text-sage hover:text-cream"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div
      className={`flex items-center rounded-full border border-gold/30 bg-moss px-1 ${className}`}
    >
      {btn("es", "ES")}
      <span className="w-px h-3 bg-gold/30" />
      {btn("en", "EN")}
    </div>
  );
}

export default function Nav() {
  const { lang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const links = [
    { id: "tratamientos", label: lang === "es" ? "Tratamientos" : "Treatments" },
    { id: "galeria", label: lang === "es" ? "La Galería" : "Gallery" },
    { id: "bono", label: lang === "es" ? "Bonos" : "Vouchers" },
    { id: "contacto", label: lang === "es" ? "Contacto" : "Contact" },
  ];

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-forest/85 backdrop-blur-xl border-b border-gold/15"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between gap-6">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.__lenis
                ? window.__lenis.scrollTo(0, { duration: 1.2 })
                : window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <span className="text-gold transition-transform duration-500 group-hover:rotate-6">
              <LotusMark className="w-7 h-7" />
            </span>
            <span className="font-serif text-[26px] tracking-[0.3em] text-cream leading-none">
              ROYALE
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={go(l.id)}
                className="relative font-mono text-[11px] tracking-[0.2em] uppercase text-sage hover:text-cream transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold hover:after:w-full after:transition-all after:duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BRAND.phoneHref}
              data-testid="nav-phone-button"
              aria-label={`Call ${BRAND.phoneDisplay}`}
              className="hidden md:flex items-center gap-2 rounded-full border border-gold/30 bg-moss px-4 py-2 font-mono text-xs tracking-wider text-cream hover:border-goldbright hover:text-goldbright transition-colors duration-300"
            >
              <Phone className="w-3.5 h-3.5 text-gold" />
              {BRAND.phoneDisplay}
            </a>
            <LangToggle className="hidden sm:flex" />
            <a
              href={BRAND.fresha}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-fresha"
              className="hidden sm:inline-flex items-center rounded-full bg-gold px-5 py-2.5 text-[13px] font-medium tracking-wide text-forest hover:bg-goldbright hover:scale-[1.03] active:scale-[0.98] transition-transform duration-300"
            >
              {lang === "es" ? "Reservar" : "Book now"}
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full border border-gold/30 text-cream hover:border-goldbright transition-colors duration-300"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 z-40 lg:hidden bg-forest/97 backdrop-blur-xl pt-24 px-6"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {links.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={go(l.id)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.35 }}
                  className="font-serif text-4xl text-cream py-3 border-b border-gold/15"
                >
                  {l.label}
                </motion.a>
              ))}
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <a
                href={BRAND.fresha}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-fresha"
                className="inline-flex items-center justify-center rounded-full bg-gold px-6 py-4 font-medium text-forest"
              >
                {lang === "es" ? "Reservar en Fresha" : "Book on Fresha"}
              </a>
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-whatsapp"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-wamist/60 text-wamist px-6 py-4 font-medium"
              >
                WhatsApp · {BRAND.phoneDisplay}
              </a>
              <LangToggle className="mt-4 self-start" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
